import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import * as AdminModels from '../../../models/AdminModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { AdminUserSessionContext } from '../../../viewmodels/AdminViewModels';
import { invalidateIndustryAutomationTemplateQuery } from './useIndustryAutomationTemplateQuery';
import { invalidateAllIndustryAutomationTemplatesQueries } from './useIndustryAutomationTemplatesQuery';

interface IParams {
	templateId: string;
	industries: Api.Industry[];
	status?: Api.TemplateStatus;
	automationTemplate?: Api.IAutomationTemplate;
}

export function useSetIndustryAutomationTemplateIndustriesMutation({
	onError,
	onSuccess,
}: {
	onError?: (err: Api.IOperationResultNoValue) => void;
	onSuccess?: (res: AdminModels.ISystemAutomationTemplate) => void;
} = {}) {
	const userSession = useUserSession<AdminUserSessionContext>();
	return useMutation({
		mutationFn: async ({ templateId, industries, status, automationTemplate }: IParams) => {
			if (status === Api.TemplateStatus.Published) {
				if (automationTemplate?.status === Api.TemplateStatus.Draft || automationTemplate?.draft) {
					await userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
						Api.ImpersonationBroker.composeApiUrl({
							urlPath: `AutomationTemplate/${encodeURIComponent(templateId)}/Draft/commit`,
						}),
						'POST'
					);
				}
			}

			const url = status
				? `admin/AutomationTemplate/${templateId}/Industry?status=${encodeURIComponent(status)}`
				: `admin/AutomationTemplate/${templateId}/Industry`;

			const result = await userSession.webServiceHelper.callAsync<AdminModels.ISystemAutomationTemplate>(
				url,
				'PUT',
				industries
			);

			return result;
		},
		onError,
		onSuccess: (data, variables) => {
			invalidateIndustryAutomationTemplateQuery(variables.templateId);
			invalidateAllIndustryAutomationTemplatesQueries();
			onSuccess?.(data);
		},
	});
}
